import React, { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import PropTypes from 'prop-types';

import { FormRow, FormLabel, FormSearchInput } from 'components/FormComponents';
import { MainButton } from 'components/StyledComponents';
import DialogHeader from 'components/DialogComponents/DialogHeader';
import DialogWrapper from 'components/DialogComponents/DialogWrapper';
import ToggleBar from 'components/ToggleBars/ToggleBar';

import { useMobileViewport } from 'hooks/useMobileViewport';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { getPicklistAssemblies } from 'actions/addAssemblyPopupActions';
import { getSingleAssemble } from 'actions/assembledBOMActions';

import { ASSEMBLY_DOES_NOT_CONTAIN_ANY_ASSETS } from 'constants/infoSnackbarData';

import { enqueueErrorSnackbar } from 'helpers/AppHelpers';

import AssetPrefixesTable from './components/AssetPrefixesTable';
import LooseItemsTable from './components/LooseItemsTable';

const SCREENS = {
  PICKLIST: 'Picklist Detailed'
};
const TABS = {
  ASSETS: 'Assets',
  LOOSE_ITEMS: 'Loose Items'
};
const TOGGLE_BUTTONS = [
  { id: 1, label: 'Assets', value: TABS.ASSETS },
  { id: 2, label: 'Loose Items', value: TABS.LOOSE_ITEMS }
];

/**
 * @typedef {Object} AddAssemblyPopupProps
 * @property {boolean} open
 * @property {() => void} setOpen
 * @property {'Picklist Detailed' | 'Leavelist Detailed'} screen
 * @property {() => void} onAdd
 */

/**
 * @param {AddAssemblyPopupProps} props
 * @returns {JSX.Element}
 */

function AddAssemblyPopup({ open, setOpen, screen, onAdd }) {
  const isMobile = useMobileViewport();
  const dispatch = useDispatch();

  const [options, setOptions] = useState({ assemblies: [], assetPrefixes: [], looseItems: [] });
  const [toggleState, setToggleState] = useState({ active: false, tab: TABS.ASSETS });

  const methods = useForm({ defaultValues: { assembly: {} }, mode: 'onChange' });
  const { setValue, getValues } = methods;

  const { assemblies, assetPrefixes, looseItems } = options;

  const isPicklist = screen === SCREENS.PICKLIST;
  const title = isMobile ? 'Add Assembly' : `Add Assembly to ${isPicklist ? 'Picklist' : ''}`;

  useEffect(() => {
    dispatch(getPicklistAssemblies({ filters: { isPicklist } })).then((res) =>
      setOptions((prev) => ({ ...prev, assemblies: res?.items || [] }))
    );
  }, []);

  const changeTab = (tab) => setToggleState((prev) => ({ ...prev, tab }));

  const closePopup = () => setOpen(false);
  const handleAddClick = () => {
    onAdd(getValues('assembly'));
    closePopup();
  };

  const setAssemblyItems = (assetPrefixes, looseItems) =>
    setOptions((prev) => ({ ...prev, assetPrefixes, looseItems }));

  useEffect(() => {
    const hasAssets = assetPrefixes?.length > 0;
    const hasLooseItems = looseItems?.length > 0;

    if (hasAssets && hasLooseItems) {
      setToggleState({ active: true, tab: TABS.ASSETS });
    } else if (hasAssets) {
      setToggleState({ active: false, tab: TABS.ASSETS });
    } else if (hasLooseItems) {
      setToggleState({ active: false, tab: TABS.LOOSE_ITEMS });
    } else {
      setToggleState({ active: false, tab: '' });
    }
  }, [assetPrefixes, looseItems]);

  const selectAssembly = async (name, value) => {
    setValue(name, value);

    if (!value?.id) return;

    const response = await dispatch(getSingleAssemble(value.id));

    const { assetPrefixes = [], looseItems = [] } = response;

    setAssemblyItems(assetPrefixes, looseItems);

    if (!assetPrefixes?.length && !looseItems?.length) {
      enqueueErrorSnackbar(ASSEMBLY_DOES_NOT_CONTAIN_ANY_ASSETS);
    }
  };

  return (
    <DialogWrapper open={open} onClose={closePopup}>
      <div className={styles.popup}>
        <DialogHeader title={title} onClose={closePopup} />
        <div className={styles.content}>
          <FormProvider {...methods}>
            <div className={styles.form}>
              <FormRow>
                <FormLabel required>Assembly Name</FormLabel>
                <FormSearchInput
                  name="assembly"
                  options={assemblies || []}
                  onSelect={selectAssembly}
                />
              </FormRow>
            </div>
          </FormProvider>

          {toggleState.active && (
            <ToggleBar
              buttons={TOGGLE_BUTTONS}
              onChange={changeTab}
              selectedTab={toggleState.tab}
              classes={styles.toggle}
            />
          )}

          {toggleState.tab === TABS.ASSETS && <AssetPrefixesTable assetPrefixes={assetPrefixes} />}

          {toggleState.tab === TABS.LOOSE_ITEMS && <LooseItemsTable looseItems={looseItems} />}
        </div>
        <div className={styles.footer}>
          <MainButton text="Cancel" action={closePopup} type="secondary" />
          <MainButton
            text="Add"
            action={handleAddClick}
            type="primary"
            isDisabled={
              !getValues('assembly')?.id || (!assetPrefixes?.length && !looseItems?.length)
            }
          />
        </div>
      </div>
    </DialogWrapper>
  );
}

AddAssemblyPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  screen: PropTypes.string.isRequired,
  onAdd: PropTypes.func.isRequired
};

export { AddAssemblyPopup };
